import React, { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { getEquipmentsAction, deleteEquipmentAction } from '../../../store/equipment'
import { getEquipmentInventoriesAction } from '../../../store/inventory'
import { EquipmentsTable } from '../EquipmentsTable'
import { AddEquipmentModal, EditEquipmentModal } from '../EquipmentFormModal'
import {
  ReceiptInventoryOperationModal,
  MoveInventoryOperationModal,
  SaleInventoryOperationModal,
  DisposalInventoryOperationModal,
} from '../InventoryOperation'
import { OPERATION_RECEIPT, OPERATION_MOVE, OPERATION_SALE, OPERATION_DISPOSAL, OPERATIONS } from '../constants'
import ProtectedAccess from '../../RoleBasedAccess/ProtectedAccess'

const { confirm } = Modal

const EquipmentTabView = () => {
  const dispatch = useDispatch()

  const [viewEquipmentId, setViewEquipmentId] = useState(null)
  const [operationIds, setOperationIds] = useState([])
  const [showModalSettings, setShowModalSettings] = useState({
    add: false,
    edit: false,
    [OPERATION_RECEIPT]: false,
    [OPERATION_MOVE]: false,
    [OPERATION_SALE]: false,
    [OPERATION_DISPOSAL]: false,
  })

  const loadEquipmentData = useCallback(
    () => dispatch(getEquipmentsAction({ pagination: { page: 1, pageSize: 10 } })),
    [dispatch]
  )

  const loadEquipmentInventoriesData = useCallback(
    (equipments) => {
      for (let equipment of equipments) dispatch(getEquipmentInventoriesAction(equipment))
    },
    [dispatch]
  )

  const showAddModal = useCallback(() => setShowModalSettings((prev) => ({ ...prev, add: true })), [])
  const hideAddModal = useCallback(() => setShowModalSettings((prev) => ({ ...prev, add: false })), [])

  const showEditModal = useCallback(({ id }) => {
    setViewEquipmentId(id)
    setShowModalSettings((prev) => ({ ...prev, edit: true }))
  }, [])

  const hideEditModal = useCallback(() => {
    setViewEquipmentId(null)
    setShowModalSettings((prev) => ({ ...prev, edit: false }))
  }, [])

  const onNewEquipment = useCallback(() => {
    loadEquipmentData()
    hideAddModal()
  }, [])

  const onUpdateEquipment = useCallback(() => {
    loadEquipmentData()
    hideEditModal()
  }, [])

  const showInvOpModal = useCallback((ids, type) => {
    setOperationIds(ids)
    setShowModalSettings((prev) => ({ ...prev, [type]: true }))
  }, [])

  const hideInvOpModal = useCallback((type) => {
    setOperationIds([])
    setShowModalSettings((prev) => ({ ...prev, [type]: false }))
  }, [])

  const onInvOpModalFinish = useCallback((ids, type) => {
    loadEquipmentData()
    loadEquipmentInventoriesData(ids)
    hideInvOpModal(type)
  }, [])

  const showDeleteConfirm = useCallback(
    ({ id, name }) => {
      confirm({
        title: 'Удалить оборудование',
        icon: <ExclamationCircleOutlined />,
        content: `Оборудование ${name}`,
        okText: 'Да',
        okType: 'danger',
        cancelText: 'Нет',
        onOk() {
          return dispatch(deleteEquipmentAction(id))
            .unwrap()
            .then(() => loadEquipmentData())
        },
        onCancel() {
          // nothing
        },
      })
    },
    [dispatch]
  )

  const handleInventoryOperation = (ids, type) => {
    if (Object.keys(OPERATIONS).includes(type)) showInvOpModal(ids, type)
    else console.error(`Операция ${type} не реализована`)
  }

  return (
    <div>
      <EquipmentsTable
        addNewAction={showAddModal}
        onEditRecord={showEditModal}
        onDeleteRecord={showDeleteConfirm}
        onInventoryOperation={handleInventoryOperation}
      />
      <AddEquipmentModal showModal={showModalSettings.add} onCancel={hideAddModal} onFinish={onNewEquipment} />
      <EditEquipmentModal
        equipmentId={viewEquipmentId}
        showModal={showModalSettings.edit}
        onCancel={hideEditModal}
        onFinish={onUpdateEquipment}
      />
      <ReceiptInventoryOperationModal
        equipmentIds={operationIds}
        showModal={showModalSettings[OPERATION_RECEIPT]}
        onCancel={() => hideInvOpModal(OPERATION_RECEIPT)}
        onFinish={(equipments) => onInvOpModalFinish(equipments, OPERATION_RECEIPT)}
      />
      <MoveInventoryOperationModal
        equipmentIds={operationIds}
        showModal={showModalSettings[OPERATION_MOVE]}
        onCancel={() => hideInvOpModal(OPERATION_MOVE)}
        onFinish={(equipments) => onInvOpModalFinish(equipments, OPERATION_MOVE)}
      />
      <SaleInventoryOperationModal
        equipmentIds={operationIds}
        showModal={showModalSettings[OPERATION_SALE]}
        onCancel={() => hideInvOpModal(OPERATION_SALE)}
        onFinish={(equipments) => onInvOpModalFinish(equipments, OPERATION_SALE)}
      />
      <DisposalInventoryOperationModal
        equipmentIds={operationIds}
        showModal={showModalSettings[OPERATION_DISPOSAL]}
        onCancel={() => hideInvOpModal(OPERATION_DISPOSAL)}
        onFinish={(equipments) => onInvOpModalFinish(equipments, OPERATION_DISPOSAL)}
      />
    </div>
  )
}

export default EquipmentTabView
